// --- Dependencies -------------------------------------------------------------------------------
import { create } from "zustand";
import { persist } from "zustand/middleware";
// --- Our Modules --------------------------------------------------------------------------------
import type { TyClient, TyMembership } from "@/types";
// --- Logic --------------------------------------------------------------------------------------
type TyAccount = { token: string; client: TyClient };

type TyAuthStore = {
    token: string | null;
    client: TyClient | null;
    memberships: TyMembership[];
    /** Every identity signed in on this device. Offices share machines; a shift change is a switch, not a sign-in. */
    accounts: TyAccount[];
    setToken: (token: string) => void;
    setSession: (client: TyClient, memberships: TyMembership[]) => void;
    /**
     * The person edited their own details. Only the account is replaced — the memberships did not
     * change, and re-reading them would make the sidebar and the company badge flicker for a
     * renamed avatar. The stored copy in `accounts` moves with it, or a shift change would bring
     * back the old name.
     */
    updateClient: (client: TyClient) => void;
    /**
     * One organization's own details changed — its name, its logo, its colour.
     *
     * The memberships are read once, at sign-in, and everything that draws a company reads them:
     * the sidebar, the switcher, the badge. So a person who changed the logo on the settings screen
     * kept seeing the old one until they signed in again. This replaces that one entry in place.
     */
    patchOrganization: (organizationId: string, patch: Partial<TyMembership["organization"]>) => void;
    switchAccount: (clientId: string) => void;
    logout: () => void;
};

const useAuth = create<TyAuthStore>()(
    persist(
        (set, get) => ({
            token: null,
            client: null,
            memberships: [],
            accounts: [],

            setToken: (token) => set({ token }),

            setSession: (client, memberships) =>
                set((state) => {
                    const others = state.accounts.filter((account) => account.client._id !== client._id);
                    const accounts = state.token ? [...others, { token: state.token, client }] : others;
                    return { client, memberships, accounts };
                }),

            updateClient: (client) =>
                set((state) => ({
                    client,
                    accounts: state.accounts.map((account) => (account.client._id === client._id ? { ...account, client } : account)),
                })),

            patchOrganization: (organizationId, patch) =>
                set((state) => ({
                    memberships: state.memberships.map((membership) =>
                        membership.organization._id === organizationId
                            ? { ...membership, organization: { ...membership.organization, ...patch } }
                            : membership
                    ),
                })),

            switchAccount: (clientId) => {
                const account = get().accounts.find((item) => item.client._id === clientId);
                if (account) set({ token: account.token, client: account.client, memberships: [] });
            },

            logout: () =>
                set((state) => ({
                    token: null,
                    client: null,
                    memberships: [],
                    accounts: state.accounts.filter((account) => account.client._id !== state.client?._id),
                })),
        }),
        { name: "auth" }
    )
);

export default useAuth;
